import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  type PropsWithChildren,
} from 'react';

import { twMerge } from '@/core/tamagoshiTailwind/util/twMerge';

import {
  getScroll,
  getScrollableVisibility,
  getScrollMax,
} from './scrollableUtils';

const SCROLL_MIN = 0;
const FULL_VISIBILITY_THRESHOLD = 0.99;

export type Direction = 'right' | 'left';

export interface DirectionOptions {
  scrollBy?: number | { (): number };
}

export interface ScrollState {
  index: number;
  position: number;
  min: number;
  max: number;
}

export interface ScrollableProps extends PropsWithChildren {
  className?: string;

  onScrollChange?(state: ScrollState): void;
}

export interface ScrollableHandle {
  scrollTowards(direction: Direction, options?: DirectionOptions): void;

  scrollToIndex(index: number, smooth?: boolean): void;

  scrollToPosition(position: number, smooth?: boolean): void;
}

/**
 * A scrollable container that exposes scroll handles in `ScrollableHandle`.
 *
 * It only implements scrolling behaviors and does not customize the styles.
 * See the `Playlist` component for the design system playlist.
 */
export const Scrollable = forwardRef<ScrollableHandle, ScrollableProps>(
  ({ className, onScrollChange = () => null, children }, forwardedRef) => {
    const scrollable = useRef<HTMLDivElement>(null);

    const updateScrollState = () => {
      const sv = getScrollableVisibility(scrollable);

      let leftSlideIndex = 0;
      if (sv?.children) {
        while (
          leftSlideIndex + 1 < sv.children.length &&
          sv.children[leftSlideIndex + 1].visibility >=
            FULL_VISIBILITY_THRESHOLD
        ) {
          leftSlideIndex++;
        }
      }

      const state = {
        index: leftSlideIndex,
        position: getScroll(scrollable),
        min: SCROLL_MIN,
        max: getScrollMax(scrollable),
      };

      onScrollChange(state);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onScroll = useCallback(updateScrollState, []);

    /**
     * listen to scroll event
     */
    useEffect(() => {
      let timer: number;
      const debouncedOnScroll = () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(onScroll, 16);
      };

      const currentScrollable = scrollable && scrollable.current;
      if (currentScrollable) {
        currentScrollable.addEventListener('scroll', debouncedOnScroll);
        currentScrollable.scrollLeft = 0;
      }

      return () => {
        if (currentScrollable) {
          currentScrollable.removeEventListener('scroll', debouncedOnScroll);
        }
      };
    }, [scrollable, onScroll]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => updateScrollState(), [children]);

    useImperativeHandle(
      forwardedRef,
      () => ({
        scrollTowards(direction: Direction): void {
          const sv = getScrollableVisibility(scrollable);

          if (sv) {
            if (direction === 'right') {
              // 1. find the last non-fully visible item starting from the end
              // If it's visibility is zero, we continue searching,
              // except if the next was already fully visible

              let firstElementToMakeVisible = sv.children.length - 1;
              while (
                sv.children[firstElementToMakeVisible].visibility === 0 &&
                firstElementToMakeVisible - 1 >= 0 &&
                sv.children[firstElementToMakeVisible - 1].visibility <
                  FULL_VISIBILITY_THRESHOLD
              ) {
                firstElementToMakeVisible--;
              }

              // 2. find the last item to make visible after next scroll
              // This is the farthest one, that will snap on the right,
              // while firstElementToMakeVisible will remain fully visible

              let lastElementToMakeVisible =
                sv.children[firstElementToMakeVisible].visibility >=
                FULL_VISIBILITY_THRESHOLD
                  ? Math.min(
                      firstElementToMakeVisible + 1,
                      sv.children.length - 1,
                    )
                  : firstElementToMakeVisible;
              while (
                lastElementToMakeVisible + 1 < sv.children.length &&
                sv.children[lastElementToMakeVisible + 1].x -
                  sv.children[firstElementToMakeVisible].x +
                  sv.children[lastElementToMakeVisible + 1].width <=
                  sv.scrollableRect.width - 2 * sv.scrollablePadding
              ) {
                lastElementToMakeVisible++;
              }

              // 3. scroll so the last item is at the right edge

              const nextScroll =
                sv.scrollableScroll +
                sv.children[lastElementToMakeVisible].x +
                sv.children[lastElementToMakeVisible].width -
                (sv.scrollableRect.width - sv.scrollablePadding);

              return scrollable?.current?.scrollTo({
                left: nextScroll,
                behavior: 'auto',
              });
            }

            if (direction === 'left') {
              // 1. find the first non-fully visible item

              let firstElementToMakeVisible = 0;
              while (
                sv.children[firstElementToMakeVisible].visibility === 0 &&
                firstElementToMakeVisible + 1 < sv.children.length &&
                sv.children[firstElementToMakeVisible + 1].visibility <
                  FULL_VISIBILITY_THRESHOLD
              ) {
                firstElementToMakeVisible++;
              }

              // 2. find the last item possibly visible after next scroll

              let lastElementToMakeVisible =
                sv.children[firstElementToMakeVisible].visibility >=
                FULL_VISIBILITY_THRESHOLD
                  ? Math.max(firstElementToMakeVisible - 1, 0)
                  : firstElementToMakeVisible;
              while (
                lastElementToMakeVisible - 1 >= 0 &&
                sv.children[firstElementToMakeVisible].x +
                  sv.children[firstElementToMakeVisible].width -
                  sv.children[lastElementToMakeVisible - 1].x <=
                  sv.scrollableRect.width - 2 * sv.scrollablePadding
              ) {
                lastElementToMakeVisible--;
              }

              // 3. scroll so the last item is at the left edge

              const nextScroll =
                sv.scrollableScroll +
                sv.children[lastElementToMakeVisible].x -
                sv.scrollablePadding;

              return scrollable?.current?.scrollTo({
                left: nextScroll,
                behavior: 'auto',
              });
            }
          }
        },
        scrollToIndex(index: number, smooth = true) {
          const sv = getScrollableVisibility(scrollable);

          if (sv?.children && index >= 0 && index < sv.children.length) {
            return scrollable?.current?.scrollTo({
              left:
                sv.children[index].x +
                sv.scrollableScroll -
                sv.scrollablePadding,
              behavior: smooth ? 'auto' : 'instant',
            });
          }
        },
        scrollToPosition(position: number, smooth = true) {
          return scrollable?.current?.scrollTo({
            left: position,
            behavior: smooth ? 'auto' : 'instant',
          });
        },
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [scrollable],
    );

    return (
      <div
        ref={scrollable}
        className={twMerge(
          `hide-scrollbar flex w-full flex-row flex-nowrap items-stretch
          justify-start overflow-y-hidden overflow-x-scroll overscroll-x-contain
          scroll-smooth *:shrink-0`,
          className,
        )}
        role="list"
      >
        {children}
      </div>
    );
  },
);

Scrollable.displayName = 'Scrollable';
